import React, { useState, useEffect } from "react";
import { userService } from "../../../_services";
import { FormDialog } from "@/components/ui/form-dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronUp, ChevronDown, CheckCircle, AlertCircle } from "lucide-react";

/**
 * Sort Videos Modal
 * Modernized with shadcn Dialog - reorder videos within a module
 */
function SortVideos({ hideModal, moduleData, renderVideos }) {
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [moduleTitle, setModuleTitle] = useState('');
    const [moduleVideoList, setModuleVideoList] = useState([]);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        setUserId(userId);
        setUserName(userName);

        if (moduleData) {
            setModuleTitle(moduleData.moduleName);
            setModuleVideoList(moduleData.media || []);
        }
    }, [moduleData]);

    const swapUp = (currentIndex) => {
        const ItemCount = moduleVideoList.length;
        let listData = [...moduleVideoList];

        if (currentIndex === 0) {
            // Move first item to last
            const firstItem = listData[0];
            listData[0] = listData[ItemCount - 1];
            listData[ItemCount - 1] = firstItem;
        } else {
            // Swap with previous item
            const temp = listData[currentIndex];
            listData[currentIndex] = listData[currentIndex - 1];
            listData[currentIndex - 1] = temp;
        }

        setModuleVideoList(listData);
    };

    const swapDown = (currentIndex) => {
        const ItemCount = moduleVideoList.length;
        let listData = [...moduleVideoList];

        if (currentIndex + 1 === ItemCount) {
            // Move last item to first
            const lastItem = listData[currentIndex];
            listData[currentIndex] = listData[0];
            listData[0] = lastItem;
        } else {
            // Swap with next item
            const temp = listData[currentIndex];
            listData[currentIndex] = listData[currentIndex + 1];
            listData[currentIndex + 1] = temp;
        }

        setModuleVideoList(listData);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            const ModuleId = moduleData.objectUID;
            const alteredList = moduleVideoList.map((video) => ({ ...video, mediaBase64: '-' }));
            const SwappingJson = JSON.stringify(alteredList);

            const apidata = await userService.Swap_Media(userId, userName, ModuleId, encodeURIComponent(SwappingJson));

            if (apidata && apidata[0].code === '1') {
                setFormSuccess(apidata[0].message);
                setTimeout(() => {
                    hideModal();
                    renderVideos(userId, userName);
                }, 3000);
            } else {
                setFormError('Error saving order');
            }
        } catch (error) {
            setFormError('An error occurred while saving');
            console.error('Error sorting videos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        hideModal();
    };

    return (
        <FormDialog
            open={true}
            onOpenChange={handleClose}
            title={`Reorder Videos - ${moduleTitle}`}
            description="Use the arrows to reorder videos. Changes will be saved when you click 'Save Order'."
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitText="Save Order"
            loading={loading}
            maxWidth="lg"
        >
            <div className="space-y-4">
                {moduleVideoList.length > 0 ? (
                    <div className="space-y-2">
                        {moduleVideoList.map((video, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-3 p-3 border border-border rounded-lg bg-card hover:bg-muted transition-colors"
                            >
                                <div className="flex flex-col gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => swapUp(index)}
                                        className="h-6 w-6 hover:bg-accent/10"
                                    >
                                        <ChevronUp className="h-4 w-4 text-primary" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => swapDown(index)}
                                        className="h-6 w-6 hover:bg-accent/10"
                                    >
                                        <ChevronDown className="h-4 w-4 text-primary" />
                                    </Button>
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-primary">{video.mediaTitle}</p>
                                    <p className="text-sm text-primary/70">Position: {index + 1}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-primary/70 py-4">No videos to sort</p>
                )}

                {/* Error Message */}
                {formError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                )}

                {/* Success Message */}
                {formSuccess && (
                    <Alert variant="success">
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>{formSuccess}</AlertDescription>
                    </Alert>
                )}
            </div>
        </FormDialog>
    );
}

export default SortVideos;

